'use client';

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Home, RefreshCw, Bug } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Next.js Error Boundary Page Component
 * Automatically catches and handles client-side errors
 */
export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const { t, isRTL } = useCommonTranslation();
  const [countdown, setCountdown] = useState(5);

  // Check if this is a chunk load error
  const isChunkError = error.name === 'ChunkLoadError' ||
                       error.message?.includes('Failed to load chunk') ||
                       error.message?.includes('Loading chunk');

  // Auto-reload for chunk errors after countdown
  useEffect(() => {
    if (!isChunkError || typeof window === 'undefined') return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isChunkError]);

  const handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/dashboard';
    }
  };

  const handleRefreshPage = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const getErrorType = (error: Error): string => {
    const message = error.message.toLowerCase();
    if (isChunkError) return 'chunk';
    if (message.includes('network') || message.includes('fetch')) return 'network';
    if (message.includes('permission') || message.includes('unauthorized')) return 'permission';
    if (message.includes('not found') || message.includes('404')) return 'notFound';
    if (message.includes('server') || message.includes('500')) return 'server';
    if (message.includes('validation')) return 'validation';
    return 'generic';
  };

  const errorType = getErrorType(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <AlertTriangle className="h-8 w-8 text-red-500" />
            <Badge variant={isChunkError ? 'secondary' : 'destructive'} className="flex items-center gap-1">
              <Bug className="h-3 w-3" />
              {isChunkError ? 'App Update' : t('errors.boundary.title')}
            </Badge>
          </div>
          
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t(`errors.${errorType}.title`)}
          </CardTitle>
          
          <CardDescription className="text-lg mt-2">
            {isChunkError
              ? `The application has been updated. Reloading in ${countdown} seconds...`
              : t(`errors.${errorType}.description`)}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Error Actions */}
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={reset} className="min-w-[140px]">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('errors.boundary.actions.retry')}
            </Button>
            
            <Button onClick={handleGoHome} variant="outline" className="min-w-[140px]">
              <Home className="h-4 w-4 mr-2" />
              {t('errors.boundary.actions.goHome')}
            </Button>
            
            <Button onClick={handleRefreshPage} variant="outline" className="min-w-[140px]">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('errors.boundary.actions.refresh')}
            </Button>
          </div>

          {/* Error Details - Always show */}
          <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg space-y-3">
            <div>
              <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                {t('errors.boundary.details')}:
              </h4>
              <p className="text-sm text-red-600 dark:text-red-400 font-mono mt-1 break-all">
                {error.message}
              </p>
            </div>

            {error.name && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Error Type:</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 font-mono mt-1">
                  {error.name}
                </p>
              </div>
            )}

            {error.digest && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Error Digest:</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 font-mono mt-1">
                  {error.digest}
                </p>
              </div>
            )}

            {error.stack && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Stack Trace:</h4>
                <pre className="text-xs text-gray-600 dark:text-gray-300 font-mono mt-1 overflow-x-auto max-h-48 p-2 bg-gray-200 dark:bg-gray-900 rounded">
                  {error.stack}
                </pre>
              </div>
            )}
          </div>

          {/* Help Text */}
          <div className="text-center text-sm text-gray-600 dark:text-gray-300">
            <p>If this problem persists, please contact support with the error details above.</p>
            <p className="mt-1">Error ID: {error.name}-{Date.now()}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}